import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ExportModal = ({ isOpen, onClose, vehicleData, onExport }) => {
  const [exportFormat, setExportFormat] = useState('json');
  const [selectedSections, setSelectedSections] = useState({
    identification: true,
    equipment: true,
    commercial: true,
    metadata: false
  });
  const [isExporting, setIsExporting] = useState(false);

  if (!isOpen) return null;

  const exportFormats = [
    {
      id: 'json',
      name: 'JSON',
      description: 'Formato estructurado para desarrollo',
      icon: 'Code',
      extension: '.json'
    },
    {
      id: 'csv',
      name: 'CSV',
      description: 'Hoja de cálculo compatible',
      icon: 'FileSpreadsheet',
      extension: '.csv'
    },
    {
      id: 'xlsx',
      name: 'Excel',
      description: 'Archivo de Microsoft Excel',
      icon: 'FileText',
      extension: '.xlsx'
    }
  ];

  const exportSections = [
    {
      id: 'identification',
      name: 'Identificación del Vehículo',
      description: 'Marca, modelo, año, VIN, etc.',
      icon: 'Car'
    },
    {
      id: 'equipment',
      name: 'Equipamiento',
      description: 'Características y opciones disponibles',
      icon: 'List'
    },
    {
      id: 'commercial',
      name: 'Valores Comerciales',
      description: 'Precios y información de mercado',
      icon: 'DollarSign'
    },
    {
      id: 'metadata',
      name: 'Metadatos',
      description: 'Información de consulta y auditoría',
      icon: 'Info'
    }
  ];

  const handleSectionToggle = (sectionId) => {
    setSelectedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedSections)?.every(Boolean);
    const newState = {};
    exportSections?.forEach(section => {
      newState[section.id] = !allSelected;
    });
    setSelectedSections(newState);
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const exportData = {
        format: exportFormat,
        sections: selectedSections,
        vehicleData,
        timestamp: new Date()?.toISOString()
      };
      
      onExport(exportData);
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const selectedCount = Object.values(selectedSections)?.filter(Boolean)?.length;
  const selectedFormat = exportFormats?.find(f => f?.id === exportFormat);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
              <Icon name="Download" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Exportar Datos</h2>
              <p className="text-sm text-muted-foreground">
                VIN: {vehicleData?.vin || 'N/A'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            iconSize={20}
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Format Selection */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-foreground mb-3">Formato de Exportación</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {exportFormats?.map((format) => (
                <div
                  key={format?.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-smooth ${
                    exportFormat === format?.id
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setExportFormat(format?.id)}
                >
                  <div className="flex items-center space-x-3">
                    <Icon name={format?.icon} size={20} className="text-primary" />
                    <div className="flex-1">
                      <div className="font-medium text-foreground">{format?.name}</div>
                      <div className="text-xs text-muted-foreground">{format?.description}</div>
                    </div>
                    {exportFormat === format?.id && (
                      <Icon name="CheckCircle" size={16} className="text-primary" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section Selection */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-foreground">Secciones a Incluir</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                iconName={Object.values(selectedSections)?.every(Boolean) ? "Square" : "CheckSquare"}
                iconSize={14}
              >
                {Object.values(selectedSections)?.every(Boolean) ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
              </Button>
            </div>
            
            <div className="space-y-3">
              {exportSections?.map((section) => (
                <div
                  key={section?.id}
                  className="flex items-center space-x-3 p-3 border border-border rounded-lg"
                >
                  <Checkbox
                    checked={selectedSections?.[section?.id]}
                    onChange={(e) => handleSectionToggle(section?.id)}
                  />
                  <Icon name={section?.icon} size={16} className="text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium text-foreground">{section?.name}</div>
                    <div className="text-sm text-muted-foreground">{section?.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Export Preview */}
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Eye" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Vista Previa de Exportación</span>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <div>Formato: <span className="font-medium text-foreground">{selectedFormat?.name}</span></div>
              <div>Secciones: <span className="font-medium text-foreground">{selectedCount} seleccionadas</span></div>
              <div>Archivo: <span className="font-mono text-foreground">
                vin-{vehicleData?.vin?.toLowerCase() || 'data'}-{new Date()?.toISOString()?.split('T')?.[0]}{selectedFormat?.extension}
              </span></div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {selectedCount} de {exportSections?.length} secciones seleccionadas
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isExporting}
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              onClick={handleExport}
              loading={isExporting}
              disabled={selectedCount === 0}
              iconName="Download"
              iconPosition="left"
              iconSize={16}
            >
              {isExporting ? 'Exportando...' : 'Exportar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;